/**
 * LandingHome — világos, prémium MEX Magic landing/login oldal.
 *
 * VALÓDI WEBES HERO KOMPOZÍCIÓ - NEM screenshot-háttér:
 *   • SVG kolibri (gradient violet → cyan, glow filter, csőre balra)
 *   • Decoratív orbital fényvonalak (SVG ringekkel)
 *   • Sparkle pontok (apró absolute div-ek)
 *   • 5 valódi HTML lebegő kártya (real markup, real content)
 *   • Glow foltok + finom grid háttér
 *
 * Route: `/` (public, NEM Layout-wrappelt - sidebar/header chrome nélkül).
 * Auth-flow: a Belépés gomb a meglévő `/login` route-ra navigál.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sparkle,
  Lightning,
  ChatCircleDots,
  User as UserIcon,
  EnvelopeSimple,
  PencilSimple,
  Tag,
  CheckCircle,
  ArrowRight,
  Heart,
  ChatCircle,
  ShareNetwork,
  Globe,
  PaperPlaneTilt,
  TrendUp,
  InstagramLogo,
  EnvelopeOpen,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import LoginModal from "../components/LoginModal";
import { playTaSound, playTammmSound, isCelebrationSoundOn } from "../lib/sound";

// 14 rövidített, stabilan törhető headline - layout-shift mentes rotációhoz
const HEADLINES = [
  "Mágnes posztok",
  "Killer webcopy",
  "Szakértői státusz",
  "Magabiztos jelenlét",
  "Bizalom mesterfokon",
  "Beszélgetésindító posztok",
  "Lélekig ható emailek",
  "Ellenállhatatlan ajánlatok",
  "Ügyfélmágnes",
  "Marketing, ami már te vagy",
  "Tartalom para nélkül",
  "Ügyfélszerzés görcs nélkül",
  "Egy hullámhosszon",
  "Szenvedélyből profit",
];

// Új feltöltött kép a Poszt-ötlet kártyához (hölgy hegyek tetején, lila naplemente)
const WOMAN_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_mex-foundation/artifacts/qd5kaklk_6.png";

/* ═══════════════════════════════════════════════════════════════════
 * AnimatedHeadline - lassú, tiszta, egységes gradient + sima fade-blur-Y.
 * 6.5s interval, 1500ms transition, expo-out easing.
 * Az egész szó EGY gradientet kap, nem karakterenként.
 * Fix méretű stack-container - SOHA nem okoz layout shiftet.
 * ═══════════════════════════════════════════════════════════════════ */
function AnimatedHeadline() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % HEADLINES.length);
    }, 6500);
    return () => clearInterval(id);
  }, []);

  return (
    <span
      data-testid="landing-headline-rotator-wrap"
      className="relative inline-block w-full"
      style={{ minHeight: "1em" }}
    >
      {/* Mind a 14 headline absolute pozícióban egymás felett.
          Csak az aktuális opacity:1 + translateY:0; a többi opacity:0 + blur. */}
      {HEADLINES.map((text, i) => {
        const active = i === index;
        return (
          <span
            key={text}
            data-testid={active ? "landing-headline-rotator" : undefined}
            aria-hidden={!active}
            aria-label={active ? text : undefined}
            className="absolute inset-0 inline-block"
            style={{
              background:
                "linear-gradient(90deg, #5e50f7 0%, #4F46E5 35%, #29BEDA 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              opacity: active ? 1 : 0,
              transform: active ? "translateY(0)" : "translateY(18px)",
              filter: active ? "blur(0)" : "blur(10px)",
              transition:
                "opacity 1500ms cubic-bezier(0.19,1,0.22,1), transform 1500ms cubic-bezier(0.19,1,0.22,1), filter 1500ms cubic-bezier(0.19,1,0.22,1)",
              willChange: "opacity, transform, filter",
              letterSpacing: "-0.035em",
              whiteSpace: "nowrap",
              paddingBottom: "0.12em",
            }}
          >
            {text}
          </span>
        );
      })}
      {/* Sizing-element - láthatatlan, csak a wrapper szélességét biztosítja */}
      <span
        aria-hidden="true"
        className="invisible inline-block"
        style={{ letterSpacing: "-0.035em", whiteSpace: "nowrap", paddingBottom: "0.12em" }}
      >
        Beszélgetésindító posztok
      </span>
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * MagicalBackdrop - a TELJES hero szekció hátterét adja (NEM panel!).
 * Rétegei:
 *   R1: világos, levendulás/off-white seamless gradient (teljes section)
 *   R2: nagyon finom, halvány grid overlay (low opacity)
 *   R3: nagy diffúz glow blob-ok - jobbra dominál, balra is átfut
 *   R4: 4–6 vékony, áramló, glowing fényvonal
 *   R5: finom sparkle pontok (kolibri körül sűrűbben)
 *   R6: alul finom ködszerű/hullámos fénytér (jobb alsó dominál, balra elsimul)
 * NEM tartalmaz: kolibrit, kártyát, szöveget, gombot.
 * Nincs külön keret, nincs téglalap-háttér: organikusan simul.
 * ═══════════════════════════════════════════════════════════════════ */
function MagicalBackdrop() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* R1: seamless levendulás alap - egységes a teljes hero-ra */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #F6F1FF 0%, #EFE7FE 24%, #ECEAFC 50%, #F1E7F8 78%, #F8EFFC 100%)",
          zIndex: 0,
        }}
      />
      {/* R2: nagyon halvány grid overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(94,80,247,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(94,80,247,0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 65% 45%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0) 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 65% 45%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0) 90%)",
          zIndex: 1,
        }}
      />
      {/* R3: nagy diffúz glow blob-ok – jobbra dominál, balra is átfut */}
      <div
        className="absolute"
        style={{
          top: "-12%",
          right: "-8%",
          width: "62%",
          height: "85%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(94,80,247,0.42) 0%, rgba(94,80,247,0.16) 38%, rgba(94,80,247,0) 72%)",
          filter: "blur(20px)",
          zIndex: 2,
        }}
      />
      <div
        className="absolute"
        style={{
          top: "8%",
          right: "20%",
          width: "44%",
          height: "55%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(41,190,218,0.38) 0%, rgba(41,190,218,0.14) 42%, rgba(41,190,218,0) 72%)",
          filter: "blur(22px)",
          zIndex: 2,
        }}
      />
      <div
        className="absolute"
        style={{
          bottom: "-5%",
          right: "-2%",
          width: "55%",
          height: "60%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(124,58,237,0.32) 0%, rgba(124,58,237,0.12) 42%, rgba(124,58,237,0) 72%)",
          filter: "blur(26px)",
          zIndex: 2,
        }}
      />
      {/* glow balra átfutó */}
      <div
        className="absolute"
        style={{
          top: "18%",
          left: "-10%",
          width: "48%",
          height: "60%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(94,80,247,0.18) 0%, rgba(94,80,247,0.06) 45%, rgba(94,80,247,0) 75%)",
          filter: "blur(28px)",
          zIndex: 2,
        }}
      />
      <div
        className="absolute"
        style={{
          bottom: "10%",
          left: "8%",
          width: "42%",
          height: "44%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(251,113,185,0.16) 0%, rgba(251,113,185,0.05) 45%, rgba(251,113,185,0) 72%)",
          filter: "blur(30px)",
          zIndex: 2,
        }}
      />
      {/* nagyon enyhe arany akcent középen */}
      <div
        className="absolute"
        style={{
          top: "38%",
          left: "44%",
          width: "26%",
          height: "26%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,219,92,0.16) 0%, rgba(255,219,92,0) 70%)",
          filter: "blur(26px)",
          zIndex: 2,
        }}
      />
      {/* DISZKRÉT EXTRA GLOW közvetlenül a kolibri mögé (mélység + fókusz) */}
      <div
        className="absolute"
        style={{
          top: "30%",
          right: "16%",
          width: "32%",
          height: "44%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(124,58,237,0.28) 0%, rgba(94,80,247,0.16) 38%, rgba(94,80,247,0) 72%)",
          filter: "blur(24px)",
          zIndex: 2,
        }}
      />

      {/* R4: áramló fényvonalak (a jobb oldalra koncentráltak, de balra is átérnek) */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        style={{ zIndex: 3 }}
      >
        <defs>
          <linearGradient id="flow-grad-1" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#5e50f7" stopOpacity="0" />
            <stop offset="35%" stopColor="#5e50f7" stopOpacity="0.42" />
            <stop offset="70%" stopColor="#29BEDA" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#29BEDA" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="flow-grad-2" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7C3AED" stopOpacity="0" />
            <stop offset="50%" stopColor="#7C3AED" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="flow-grad-3" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#29BEDA" stopOpacity="0" />
            <stop offset="50%" stopColor="#29BEDA" stopOpacity="0.36" />
            <stop offset="100%" stopColor="#29BEDA" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="flow-grad-pink" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FB7DD7" stopOpacity="0" />
            <stop offset="50%" stopColor="#FB7DD7" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#FB7DD7" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* hosszú felső ívelő szellő balról-jobbra */}
        <path
          d="M -80 220 Q 360 100 720 200 T 1520 140"
          stroke="url(#flow-grad-1)"
          strokeWidth="1.4"
          fill="none"
          opacity="0.85"
        />
        {/* középső átkelő áram */}
        <path
          d="M -40 480 Q 320 380 720 500 T 1500 380"
          stroke="url(#flow-grad-2)"
          strokeWidth="1.2"
          fill="none"
          opacity="0.7"
        />
        {/* alsó vékony ív */}
        <path
          d="M 60 760 Q 380 660 760 720 T 1480 620"
          stroke="url(#flow-grad-1)"
          strokeWidth="0.9"
          fill="none"
          opacity="0.55"
        />
        {/* jobb oldali rövidebb cián szellő */}
        <path
          d="M 700 60 Q 980 120 1180 80 T 1500 100"
          stroke="url(#flow-grad-3)"
          strokeWidth="1.0"
          fill="none"
          opacity="0.7"
        />
        {/* pink finom akcent */}
        <path
          d="M 540 820 Q 880 760 1180 800 T 1480 760"
          stroke="url(#flow-grad-pink)"
          strokeWidth="0.8"
          fill="none"
          opacity="0.6"
        />
        {/* diszkrét extra ív a kolibri irányából a headline felé (tekintetvezetés) */}
        <path
          d="M 1240 460 Q 980 420 720 480 T 200 540"
          stroke="url(#flow-grad-2)"
          strokeWidth="0.7"
          fill="none"
          opacity="0.42"
        />
      </svg>

      {/* R5: sparkle pontok – sűrűbb a kolibri körül (jobb-közép), ritkább másutt */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        style={{ zIndex: 4 }}
      >
        {[
          // sűrű cluster jobb-közép (kolibri körül)
          [880, 240, 1.6, 0.92],
          [960, 320, 1.2, 0.78],
          [1040, 200, 1.4, 0.88],
          [1120, 360, 1.0, 0.7],
          [840, 420, 1.5, 0.85],
          [920, 500, 1.1, 0.72],
          [1000, 560, 1.3, 0.82],
          [1080, 480, 1.6, 0.9],
          [1180, 300, 1.0, 0.65],
          [1260, 240, 1.3, 0.78],
          [780, 320, 1.2, 0.75],
          [820, 580, 1.4, 0.85],
          [900, 660, 1.0, 0.6],
          [980, 720, 1.2, 0.7],
          [1060, 660, 1.5, 0.86],
          [1160, 540, 1.1, 0.7],
          [1240, 460, 1.3, 0.78],
          [1340, 380, 1.0, 0.65],
          // ritkább bal/közép
          [160, 180, 1.0, 0.55],
          [280, 280, 1.2, 0.62],
          [380, 160, 1.3, 0.7],
          [520, 240, 0.9, 0.5],
          [220, 480, 1.4, 0.72],
          [340, 580, 1.0, 0.55],
          [460, 660, 1.2, 0.62],
          [560, 760, 0.9, 0.5],
          [120, 720, 1.3, 0.68],
          [480, 100, 1.0, 0.55],
          [640, 380, 1.2, 0.6],
          [580, 540, 1.1, 0.58],
        ].map(([cx, cy, r, op], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r={r} fill="#ffffff" opacity={op} />
            <circle cx={cx} cy={cy} r={r * 3} fill="#ffffff" opacity={op * 0.16} />
          </g>
        ))}
      </svg>

      {/* R6: alul finom, ködszerű hullámos fénytér (a glow blob — a hullámos
          SVG-elválasztó már a section végére van kihúzva, BottomWave-ként,
          hogy a backdrop seamless-en kiterjeszthető legyen a header alá is). */}
      <div
        className="absolute"
        style={{
          bottom: "-10%",
          right: "-5%",
          width: "70%",
          height: "32%",
          background:
            "radial-gradient(ellipse at 60% 40%, rgba(94,80,247,0.22) 0%, rgba(124,58,237,0.10) 38%, rgba(94,80,247,0) 72%)",
          filter: "blur(28px)",
          zIndex: 5,
        }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * BottomWave - a hero alját zárja le egy lágy hullámmal (a contact
 * szekció felé). Külön komponensben van, mert a MagicalBackdrop felfut
 * a header mögé is — ezt a hullámot viszont CSAK a hero alján akarjuk.
 * ═══════════════════════════════════════════════════════════════════ */
function BottomWave() {
  return (
    <svg
      aria-hidden="true"
      className="absolute bottom-0 left-0 w-full pointer-events-none"
      viewBox="0 0 1440 90"
      preserveAspectRatio="none"
      style={{ height: "80px", zIndex: 8, display: "block" }}
    >
      <path
        d="M 0 50 Q 180 0 360 40 T 720 38 T 1080 42 T 1440 28 L 1440 90 L 0 90 Z"
        fill="#F5EEFE"
      />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * HummingbirdLayer - külön TRANSZPARENS PNG kolibri asset.
 * Csőre BALRA mutat → a bal oldali animált headline felé.
 * NEM túl jobbra: ~10% offset a hero-vizuál jobb-közepétől.
 * ═══════════════════════════════════════════════════════════════════ */
function HummingbirdLayer() {
  return (
    <img
      src="https://customer-assets.emergentagent.com/job_mex-foundation/artifacts/5i8lgumc_kolibri_transparent.png"
      alt=""
      aria-hidden="true"
      className="absolute pointer-events-none lh-hummingbird"
      // ABOVE-THE-FOLD: eager + high prioritás, NEM lazy-load
      loading="eager"
      decoding="async"
      fetchPriority="high"
      // POZÍCIÓ ÉS MÉRET PONTOSAN UGYANAZ MINT KORÁBBAN
      style={{
        top: "52%",
        right: "6%",
        transform: "translateY(-50%)",
        width: "min(100%, 770px)",
        height: "auto",
        aspectRatio: "1 / 1",
        objectFit: "contain",
        filter: "drop-shadow(0 42px 80px rgba(94,80,247,0.40))",
        zIndex: 4,
        animation: "lh-hummingbird-breathe 11s ease-in-out infinite",
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * FloatingCard - üvegszerű, prémium tartalomkártya (újratervezve)
 * Nagy radius, pill label, finom árnyékok, sok whitespace.
 * ═══════════════════════════════════════════════════════════════════ */
function FloatingCard({
  style,
  label,
  labelIcon: LabelIcon,
  labelColor = "#5e50f7",
  rotate = 0,
  delay = 0,
  testid,
  positionClass = "",
  children,
}) {
  return (
    <div
      data-testid={testid}
      className={`absolute lh-card ${positionClass}`}
      style={{
        ...style,
        transform: `${style?.transform || ""} rotate(${rotate}deg)`.trim(),
        animation: `lh-float 9s ease-in-out ${delay}s infinite`,
        zIndex: 5,
      }}
    >
      <div
        className="rounded-[26px] border overflow-hidden lh-card-inner"
        style={{
          background: "rgba(255,255,255,0.94)",
          backdropFilter: "blur(22px) saturate(120%)",
          WebkitBackdropFilter: "blur(22px) saturate(120%)",
          borderColor: "rgba(232,228,250,0.95)",
          boxShadow:
            "0 30px 90px -20px rgba(60,52,137,0.22), 0 8px 24px -8px rgba(60,52,137,0.10), 0 1px 0 rgba(255,255,255,0.85) inset",
        }}
      >
        {(label || LabelIcon) && (
          <div className="px-5 pt-4 pb-1 flex items-center lh-card-label">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold tracking-[0.04em]"
              style={{
                background: `${labelColor}14`,
                color: labelColor,
              }}
            >
              {LabelIcon ? <LabelIcon size={12} weight="fill" /> : null}
              {label}
            </span>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * HeroVisual - VALÓDI webes kompozíció (NEM screenshot háttér).
 * Tartalmazza: kolibri-backdrop (beleolvad a háttérbe), 5 prémium kártya
 * ═══════════════════════════════════════════════════════════════════ */
function HeroVisual() {
  return (
    <div
      data-testid="hero-visual"
      className="relative w-full lh-hero-visual"
      style={{
        height: "min(82vh, 720px)",
        minHeight: "560px",
      }}
    >
      <style>{`
        @keyframes lh-float {
          0%, 100% { translate: 0 0; }
          50%      { translate: 0 -8px; }
        }
        @keyframes lh-hummingbird-breathe {
          0%, 100% { transform: translateY(-50%) scale(1) rotate(0deg); }
          50%      { transform: translateY(-52%) scale(1.015) rotate(-0.6deg); }
        }
        /* ───── MOBIL (<768px) BAL OLDAL: középre + nagyobb méretek ───── */
        @media (max-width: 767.98px) {
          .lh-kicker-wrap { text-align: center; }
          .lh-left-col { text-align: center; }
          .lh-left-col > p, .lh-left-col > div { margin-left: auto !important; margin-right: auto !important; }
          .lh-kicker { font-size: 14px !important; }
          .lh-headline { text-align: center !important; }
          .lh-headline-anim { font-size: 40px !important; }
          .lh-headline-fixed { font-size: 38px !important; }
          .lh-subtitle { font-size: 19px !important; text-align: center !important; max-width: 100% !important; }
          .lh-cta {
            display: inline-flex !important;
            font-size: 19px !important;
            padding: 22px 38px !important;
          }
          .lh-powered { justify-content: center !important; font-size: 16px !important; }
        }
        /* MOBIL (<768px): kompakt, levegős kompozíció */
        @media (max-width: 767.98px) {
          .lh-hero-visual { height: 820px !important; min-height: 820px !important; }
          .lh-hummingbird { width: 96% !important; right: 2% !important; }
          .lh-card { transform-origin: center center; }
          .lh-card-1 { top: 0% !important; right: 0% !important; width: 184px !important; }
          .lh-card-2 { top: 8% !important; left: 2% !important; width: 172px !important; }
          .lh-card-3 { top: 64% !important; left: -3% !important; width: 176px !important; }
          .lh-card-4 { bottom: 2% !important; left: 10% !important; width: 184px !important; }
          .lh-card-5 { bottom: 0% !important; right: 0% !important; width: 174px !important; }
          .lh-card-inner { border-radius: 20px !important; }
          .lh-card-title { font-size: 13.5px !important; }
          .lh-card-body { font-size: 11.5px !important; }
          .lh-card-btn { font-size: 11.5px !important; padding: 6px 12px !important; }
          .lh-card-media { height: 108px !important; }
          .lh-card-label { padding: 12px 16px 2px !important; }
        }
        /* KIS MOBIL (<400px) */
        @media (max-width: 399.98px) {
          .lh-hero-visual { height: 740px !important; min-height: 740px !important; }
          .lh-card-1 { width: 160px !important; }
          .lh-card-2 { width: 150px !important; }
          .lh-card-3 { width: 154px !important; }
          .lh-card-4 { width: 160px !important; left: 6% !important; }
          .lh-card-5 { width: 150px !important; }
        }
      `}</style>

      {/* RÉTEG: TRANSZPARENS KOLIBRI ASSET (csőre balra mutat a címre) */}
      <HummingbirdLayer />

      {/* RÉTEG: VALÓDI HTML kártyák - rétegelve a kolibri FÖLÖTT */}

      {/* Kártya 1: Poszt ötlet - JOBB FELSŐ */}
      <FloatingCard
        testid="card-post-idea"
        positionClass="lh-card-1"
        style={{ top: "0%", right: "0%", width: "240px" }}
        rotate={-2.2}
        delay={0}
        label="POSZT ÖTLET"
        labelIcon={Sparkle}
        labelColor="#5e50f7"
      >
        <div className="px-5 pt-2 pb-3 text-[14.5px] font-bold text-[#1B1733] leading-[1.25] tracking-[-0.01em] lh-card-title">
          Vonzó ötlet a megfelelő ügyfeleknek
        </div>
        <div
          className="mx-4 mb-3 rounded-2xl overflow-hidden h-[148px] bg-no-repeat lh-card-media"
          style={{
            backgroundImage: `url('${WOMAN_PORTRAIT}')`,
            backgroundSize: "175% auto",
            backgroundPosition: "center 58%",
          }}
        />
        <div className="px-5 pb-4 flex items-center gap-4 text-[12px] text-[#7065A0] font-medium lh-card-body">
          <span className="inline-flex items-center gap-1.5"><Heart size={13} weight="fill" color="#FB7185" /> 128</span>
          <span className="inline-flex items-center gap-1.5"><ChatCircle size={13} weight="fill" color="#5e50f7" /> 26</span>
          <span className="inline-flex items-center gap-1.5"><ShareNetwork size={13} weight="fill" color="#29BEDA" /> 8</span>
        </div>
      </FloatingCard>

      {/* Kártya 2: Hírlevél - FELJEBB és HÁTRÉBB (kolibrinek utat adva) */}
      <FloatingCard
        testid="card-newsletter"
        positionClass="lh-card-2"
        style={{ top: "-2%", left: "10%", width: "224px", opacity: 0.96 }}
        rotate={2.4}
        delay={1.2}
        label="HÍRLEVÉL"
        labelIcon={EnvelopeOpen}
        labelColor="#29BEDA"
      >
        <div className="px-5 pt-1.5 text-[14px] font-semibold text-[#1B1733] leading-snug lh-card-body">
          Szia! <span style={{ filter: "saturate(1.1)" }}>👋</span>
        </div>
        <div className="px-5 pt-1 pb-4 text-[13.5px] text-[#3F365E] leading-[1.45] lh-card-body">
          Egy gondolat, amely megváltoztathatja a napodat.
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white px-4 py-2 rounded-full lh-card-btn"
            style={{
              background: "linear-gradient(135deg, #5e50f7 0%, #4F46E5 100%)",
              boxShadow: "0 8px 20px -6px rgba(94,80,247,0.5)",
            }}
          >
            Elolvasom <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </FloatingCard>

      {/* Kártya 3: Landing oldal - LEJJEBB (kolibri körüli levegősebb kompozíció) */}
      <FloatingCard
        testid="card-landing"
        positionClass="lh-card-3"
        style={{ top: "52%", left: "-5%", width: "230px" }}
        rotate={-3.0}
        delay={2.4}
        label="LANDING OLDAL"
        labelIcon={Globe}
        labelColor="#29BEDA"
      >
        <div className="px-5 pt-2 pb-2 text-[18px] font-bold text-[#1B1733] leading-[1.1] tracking-[-0.02em] lh-card-title">
          Több ügyfél,<br />kevesebb zaj.
        </div>
        <div className="px-5 pb-4 text-[12.5px] text-[#5B5380] leading-[1.5] lh-card-body">
          Világos üzenet. Erős hatás. Folyamatos növekedés.
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white px-4 py-2 rounded-full lh-card-btn"
            style={{
              background: "linear-gradient(135deg, #29BEDA 0%, #1FA9C9 100%)",
              boxShadow: "0 8px 20px -6px rgba(41,190,218,0.5)",
            }}
          >
            Kialakítod <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </FloatingCard>

      {/* Kártya 4: Értékesítési üzenet - LEJJEBB (alsó perem felé) */}
      <FloatingCard
        testid="card-sales-msg"
        positionClass="lh-card-4"
        style={{ bottom: "-3%", left: "22%", width: "240px" }}
        rotate={1.6}
        delay={0.6}
        label="ÉRTÉKESÍTÉSI ÜZENET"
        labelIcon={PaperPlaneTilt}
        labelColor="#FB7185"
      >
        <div className="px-5 pt-1.5 text-[14px] font-semibold text-[#1B1733] leading-snug lh-card-body">
          Szia! <span style={{ filter: "saturate(1.1)" }}>👋</span>
        </div>
        <div className="px-5 pt-1 pb-4 text-[13.5px] text-[#3F365E] leading-[1.45] lh-card-body">
          Van egy ötletem, ami segíthet gyorsabban elérni a céljaidat.
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white px-4 py-2 rounded-full lh-card-btn"
            style={{
              background: "linear-gradient(135deg, #FB7185 0%, #FBA94B 100%)",
              boxShadow: "0 8px 20px -6px rgba(251,113,133,0.45)",
            }}
          >
            Megnézem <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </FloatingCard>

      {/* Kártya 5: Eredmény - JOBB ALSÓ */}
      <FloatingCard
        testid="card-result"
        positionClass="lh-card-5"
        style={{ bottom: "4%", right: "-2%", width: "226px" }}
        rotate={-1.4}
        delay={1.8}
        label="EREDMÉNY"
        labelIcon={TrendUp}
        labelColor="#5e50f7"
      >
        <div className="px-5 pt-2 pb-2 text-[18px] font-bold text-[#1B1733] leading-[1.1] tracking-[-0.02em] lh-card-title">
          Magasabb<br />konverzió
        </div>
        <div className="px-5 pb-3 text-[12.5px] text-[#5B5380] leading-[1.5] lh-card-body">
          Jobb ajánlatok, tisztább érték, több elköteleződés.
        </div>
        {/* Mini emelkedő chart */}
        <div className="px-5 pb-3">
          <svg viewBox="0 0 160 36" className="w-full h-7">
            <defs>
              <linearGradient id="chart-grad" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#5e50f7" stopOpacity="0.06" />
                <stop offset="100%" stopColor="#5e50f7" stopOpacity="0.42" />
              </linearGradient>
            </defs>
            <path d="M 0 30 L 30 25 L 60 26 L 90 18 L 120 12 L 160 4 L 160 36 L 0 36 Z" fill="url(#chart-grad)" />
            <path d="M 0 30 L 30 25 L 60 26 L 90 18 L 120 12 L 160 4" fill="none" stroke="#5e50f7" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="160" cy="4" r="2.8" fill="#5e50f7" />
          </svg>
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#5e50f7] px-4 py-2 rounded-full border border-[#E0DAFA] bg-white lh-card-btn"
            style={{ boxShadow: "0 4px 12px -4px rgba(94,80,247,0.18)" }}
          >
            Részletek <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </FloatingCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * Header
 * ═══════════════════════════════════════════════════════════════════ */
function LandingHeader({ onLoginClick }) {
  return (
    <header
      data-testid="landing-header"
      className="w-full px-6 sm:px-10 lg:px-16 pt-8 pb-2 flex items-center justify-between relative z-20"
    >
      <a href="/" className="flex items-center gap-3.5" data-testid="landing-logo">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-[0_12px_32px_rgba(94,80,247,0.36)]"
          style={{ background: "linear-gradient(135deg, #5e50f7 0%, #3a2fd3 100%)" }}
        >
          <Sparkle size={28} weight="fill" color="#fff" />
        </div>
        <div className="font-heading text-[26px] sm:text-[30px] tracking-tight text-[#2B2545] font-bold">
          MEX <span className="text-[#5e50f7]">Magic</span>
        </div>
      </a>

      <button
        onClick={onLoginClick}
        data-testid="landing-login-btn"
        className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-[14px] font-medium text-[#2B2545] bg-white border border-[#E6E1FA] hover:shadow-[0_8px_24px_rgba(94,80,247,0.14)] transition-all hover:-translate-y-0.5"
      >
        Belépés
        <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
      </button>
    </header>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * ContactForm
 * ═══════════════════════════════════════════════════════════════════ */
function ContactForm() {
  const [form, setForm] = useState({ topic: "", name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Tölts ki minden mezőt.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic.trim(),
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Köszönjük! Megkaptuk az üzeneted, hamarosan jelentkezünk.");
      setForm({ topic: "", name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Nem sikerült elküldeni. Próbáld újra.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="contact-success"
        className="max-w-xl mx-auto rounded-3xl bg-white border border-[#E6E1FA] p-10 text-center shadow-[0_30px_80px_rgba(60,52,137,0.10)]"
      >
        <div className="mx-auto mb-5 w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "rgba(94,80,247,0.10)" }}>
          <CheckCircle size={28} weight="fill" color="#5e50f7" />
        </div>
        <h3 className="font-heading text-[24px] text-[#2B2545] mb-2">Köszönjük!</h3>
        <p className="text-[15px] text-[#52525B] leading-relaxed">Megkaptuk az üzeneted, hamarosan jelentkezünk.</p>
      </div>
    );
  }

  const inputBase =
    "w-full rounded-xl bg-white border border-[#DED7F5] px-4 py-3.5 text-[15px] text-[#2B2545] placeholder:text-[#9890b8] focus:outline-none focus:border-[#5e50f7] focus:ring-4 focus:ring-[#5e50f7]/10 transition-all";

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="max-w-xl mx-auto rounded-3xl bg-white border border-[#E6E1FA] p-7 sm:p-10 shadow-[0_30px_80px_rgba(60,52,137,0.10)]">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "rgba(94,80,247,0.10)" }}>
          <ChatCircleDots size={22} color="#5e50f7" weight="fill" />
        </div>
        <div>
          <h3 className="font-heading text-[24px] sm:text-[28px] text-[#2B2545] leading-tight">Írj nekünk</h3>
          <p className="text-[13px] sm:text-[14px] text-[#6A6488] leading-relaxed">Kérdésed van, vagy szeretnél hozzáférést kérni? Írj nekünk, és visszajelzünk.</p>
        </div>
      </div>

      <div className="space-y-3.5">
        <div className="relative">
          <Tag size={16} color="#8e84b8" className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select data-testid="contact-topic" value={form.topic} onChange={onChange("topic")} className={`${inputBase} pl-11 appearance-none cursor-pointer`}>
            <option value="">Válassz témát...</option>
            <option value="Várólista / hozzáférés">Várólista / hozzáférés</option>
            <option value="Technikai kérdés">Technikai kérdés</option>
            <option value="Együttműködés">Együttműködés</option>
            <option value="Egyéb kérdés">Egyéb kérdés</option>
          </select>
        </div>
        <div className="relative">
          <UserIcon size={16} color="#8e84b8" className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input data-testid="contact-name" value={form.name} onChange={onChange("name")} placeholder="Add meg a neved" className={`${inputBase} pl-11`} required />
        </div>
        <div className="relative">
          <EnvelopeSimple size={16} color="#8e84b8" className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input data-testid="contact-email" value={form.email} onChange={onChange("email")} type="email" placeholder="email@pelda.hu" className={`${inputBase} pl-11`} required />
        </div>
        <div className="relative">
          <PencilSimple size={16} color="#8e84b8" className="absolute left-4 top-4 pointer-events-none" />
          <textarea data-testid="contact-message" value={form.message} onChange={onChange("message")} placeholder="Írd le, miben segíthetünk..." rows={5} className={`${inputBase} pl-11 resize-none`} required />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit"
          className="w-full group inline-flex items-center justify-center gap-2 mt-2 px-7 py-4 rounded-2xl font-semibold text-[15px] text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(135deg, #5e50f7 0%, #7C3AED 52%, #4F46E5 100%)",
            boxShadow: "0 16px 36px rgba(94,80,247,0.32), 0 0 0 1px rgba(255,255,255,0.20) inset",
          }}
        >
          {submitting ? "Küldés..." : "Üzenet küldése"}
          {!submitting && (<ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />)}
        </button>
      </div>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * ModalConfettiBurst — modal-local 3-hullámú CSS particle burst.
 *
 * Erős, ünnepi hatás:
 *   • 3 hullám TA-TA-TAMMM ritmusban (0 / 200 / 420ms)
 *   • Összesen ~90 particle (32 + 28 + 30) — energikus, dús
 *   • Tág legyező: -170°..+10° fan + 2-3 oldal-particle a TAMMM-ban
 *   • Hosszabb röpülés (1.8-2.8s) + lassú lehullás
 *   • Vegyes shape (kör + szalag + szögletes), széles size-range
 *   • Synchronized hang: TA-TA-TAMMM (sound.js)
 *
 * NEM canvas, NEM fullscreen, NEM body-szintű → a modal kártya saját
 * rétegeként (z-index 20) renderelődik, élesen a popup ELŐTT.
 * ═══════════════════════════════════════════════════════════════════ */
const CONFETTI_COLORS = [
  "#5e50f7", "#7C3AED", "#FFDB5C", "#FFA500", "#29BEDA", "#22D3EE",
  "#FF5A8A", "#FF3D7F", "#34D399", "#10B981", "#F472B6", "#A78BFA",
];

function ModalConfettiBurst({ active }) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (!active) { setParticles([]); return; }

    // Helper: egy hullám particle-jeinek generálása
    const makeWave = (waveId, count, baseDelay, opts) => {
      const fanMin = opts?.fanMin ?? -170;
      const fanMax = opts?.fanMax ?? 10;
      const distMin = opts?.distMin ?? 180;
      const distMax = opts?.distMax ?? 320;
      const sizeMin = opts?.sizeMin ?? 7;
      const sizeMax = opts?.sizeMax ?? 14;
      const durMin = opts?.durMin ?? 1800;
      const durMax = opts?.durMax ?? 2400;
      return Array.from({ length: count }).map((_, i) => {
        const angle = fanMin + Math.random() * (fanMax - fanMin);
        const rad = (angle * Math.PI) / 180;
        const distance = distMin + Math.random() * (distMax - distMin);
        const dx = Math.cos(rad) * distance;
        const dy = Math.sin(rad) * distance;
        const size = sizeMin + Math.random() * (sizeMax - sizeMin);
        const rotEnd = Math.random() * 1080 - 540;
        const delay = baseDelay + Math.random() * 80;
        const duration = durMin + Math.random() * (durMax - durMin);
        const color = CONFETTI_COLORS[(waveId * 7 + i) % CONFETTI_COLORS.length];
        const shape = Math.random();
        const isCircle = shape < 0.3;
        const isStrip = shape >= 0.3 && shape < 0.65;
        // Vertikális lehullás additív: a particle még tovább esik a végén
        const dropY = 60 + Math.random() * 80;
        return {
          key: `${waveId}-${i}`, dx, dy, dropY, size, rotEnd,
          delay, duration, color, isCircle, isStrip,
        };
      });
    };

    // 3 hullám TA-TA-TAMMM ritmusban
    const wave1 = makeWave(1, 32, 0,   { distMin: 160, distMax: 280, sizeMin: 7,  sizeMax: 12 });
    const wave2 = makeWave(2, 28, 200, { distMin: 180, distMax: 320, sizeMin: 8,  sizeMax: 13 });
    const wave3 = makeWave(3, 30, 420, { fanMin: -185, fanMax: 25, distMin: 220, distMax: 380, sizeMin: 9, sizeMax: 16, durMin: 2000, durMax: 2800 });
    setParticles([...wave1, ...wave2, ...wave3]);

    // ── Synchronized hangok (TA-TA-TAMMM) — ha a celebration sound on ─
    if (isCelebrationSoundOn()) {
      try { playTaSound(); } catch { /* swallow */ }
      const t1 = setTimeout(() => { try { playTaSound(); } catch { /* swallow */ } }, 200);
      const t2 = setTimeout(() => { try { playTammmSound(); } catch { /* swallow */ } }, 420);
      const cleanupSound = () => { clearTimeout(t1); clearTimeout(t2); };
      // Auto-clear az utolsó hullám + 600ms (3200ms total)
      const t3 = setTimeout(() => setParticles([]), 3200);
      return () => { cleanupSound(); clearTimeout(t3); };
    }

    const t3 = setTimeout(() => setParticles([]), 3200);
    return () => clearTimeout(t3);
  }, [active]);

  if (!particles.length) return null;
  return (
    <div
      aria-hidden="true"
      data-testid="modal-confetti-burst"
      style={{
        position: "absolute",
        left: "50%",
        top: 24,
        transform: "translateX(-50%)",
        width: 0,
        height: 0,
        pointerEvents: "none",
        zIndex: 20,
      }}
    >
      {particles.map((p) => {
        const w = p.size;
        const h = p.isCircle ? p.size : (p.isStrip ? p.size * 0.35 : p.size * 0.6);
        return (
          <span
            key={p.key}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: `${w}px`,
              height: `${h}px`,
              background: p.color,
              borderRadius: p.isCircle ? "50%" : (p.isStrip ? "1px" : "2px"),
              "--dx": `${p.dx}px`,
              "--dy": `${p.dy}px`,
              "--drop": `${p.dropY}px`,
              "--rot": `${p.rotEnd}deg`,
              animation: `mexConfettiBurst ${p.duration}ms cubic-bezier(0.18,0.7,0.3,1) ${p.delay}ms forwards`,
              willChange: "transform, opacity",
              boxShadow: `0 0 8px ${p.color}66`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes mexConfettiBurst {
          0%   { transform: translate(-50%, -50%) rotate(0deg) scale(0.5); opacity: 0; }
          8%   { opacity: 1; transform: translate(-50%, -50%) rotate(0deg) scale(1); }
          55%  { opacity: 1; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(calc(var(--rot) * 0.6)) scale(1); }
          100% {
            opacity: 0;
            transform: translate(
              calc(-50% + var(--dx)),
              calc(-50% + var(--dy) + var(--drop))
            ) rotate(var(--rot)) scale(0.9);
          }
        }
      `}</style>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 * WaitlistModal — landing várólista popup (webinar modal vizuális minta
 * 1:1), DE telefon mező nélkül + saját siker-állapottal és modal-local
 * konfetti-burst-tel. A backend `/api/waitlist/subscribe` endpoint a
 * `waitlist-magic` Kit tag-et (20479791) használja.
 * ═══════════════════════════════════════════════════════════════════ */
function WaitlistModal({ open, onClose }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [cannonOn, setCannonOn] = useState(false);
  const cardRef = useRef(null);

  const canSubmit =
    firstName.trim().length > 0 && /.+@.+\..+/.test(email) && consent && !submitting;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Modal bezárásakor: state-resetelés (kis késleltetéssel, hogy az
  // out-animáció ne villantsa vissza az űrlapot).
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => {
      setSuccess(false);
      setCannonOn(false);
      setFirstName("");
      setEmail("");
      setConsent(false);
      setFormError("");
      setConsentError(false);
    }, 250);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (firstName.trim().length === 0) {
      setFormError("Kérlek, add meg a keresztneved.");
      return;
    }
    if (!/.+@.+\..+/.test(email)) {
      setFormError("Kérlek, adj meg egy érvényes e-mail címet.");
      return;
    }
    if (!consent) {
      setConsentError(true);
      return;
    }
    setConsentError(false);

    const utm = {};
    try {
      const sp = new URLSearchParams(window.location.search);
      ["utm_source","utm_medium","utm_campaign","utm_content","utm_term"].forEach((k) => {
        const v = sp.get(k);
        if (v) utm[k] = v;
      });
    } catch { /* SSR-safety, ignore */ }

    setSubmitting(true);
    try {
      await api.post("/waitlist/subscribe", {
        first_name: firstName.trim(),
        email: email.trim().toLowerCase(),
        consent: true,
        ...utm,
      });
      setSubmitting(false);
      setSuccess(true);
      // 250ms-mal a siker-állapot rendere után indítjuk a cannon-t — így
      // a célpont-kártya (ami most a siker-state) már a helyén ül.
      setTimeout(() => setCannonOn(true), 250);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFormError(
        (typeof detail === "string" && detail) ||
        "Valami nem sikerült. Kérlek, próbáld meg újra pár pillanat múlva."
      );
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(94,80,247,0.22)",
    color: "#1B1733",
    fontSize: "15px",
    padding: "13px 15px",
    borderRadius: 12,
    outline: "none",
    width: "100%",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  };

  return (
    <div
      data-testid="waitlist-modal-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4"
      style={{
        background: "rgba(27, 23, 51, 0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        ref={cardRef}
        data-testid="waitlist-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="waitlist-modal-title"
        className="relative w-full max-w-[440px] rounded-3xl"
        style={{
          background: "linear-gradient(180deg, #FFFFFF 0%, #FBF9FF 100%)",
          border: "1px solid rgba(232,228,250,0.95)",
          boxShadow:
            "0 40px 100px -20px rgba(60,52,137,0.45), 0 12px 30px -10px rgba(60,52,137,0.20), 0 1px 0 rgba(255,255,255,0.85) inset",
          padding: "32px 30px 28px",
          overflow: "visible",
        }}
      >
        {/* Bezárás gomb */}
        <button
          type="button"
          onClick={onClose}
          data-testid="waitlist-modal-close"
          aria-label="Bezárás"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
          style={{
            background: "rgba(94,80,247,0.08)",
            color: "#5e50f7",
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          ×
        </button>

        {success ? (
          <div data-testid="waitlist-modal-success" style={{ textAlign: "center", paddingTop: 8 }}>
            <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 12 }}>🎉</div>
            <h3
              className="font-heading"
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#1B1733",
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
                marginBottom: 10,
              }}
            >
              Gratulálunk, felkerültél a MEX Magic várólistára!
            </h3>
            <p
              style={{
                fontSize: 14.5,
                color: "#52525B",
                lineHeight: 1.6,
                marginBottom: 22,
              }}
            >
              Hamarosan értesítünk a részletekről, és elsőként szólunk, amikor megnyílik a hozzáférés.
            </p>
            <button
              type="button"
              onClick={onClose}
              data-testid="waitlist-modal-success-close"
              className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-2xl font-bold text-white transition-all hover:-translate-y-0.5"
              style={{
                background:
                  "linear-gradient(135deg, #6E5BFF 0%, #5e50f7 30%, #7C3AED 65%, #4F46E5 100%)",
                boxShadow:
                  "0 24px 60px rgba(94,80,247,0.48), 0 8px 18px rgba(124,58,237,0.32), 0 0 0 1px rgba(255,255,255,0.32) inset",
                fontSize: 15,
                minWidth: 160,
              }}
            >
              Rendben
            </button>
          </div>
        ) : (
          <>
            <h3
              id="waitlist-modal-title"
              className="font-heading"
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: "#1B1733",
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
                marginBottom: 6,
              }}
            >
              Feliratkozom a várólistára
            </h3>
            <p
              style={{
                fontSize: 13.5,
                color: "#6F6790",
                lineHeight: 1.55,
                marginBottom: 20,
              }}
            >
              Elsőként értesítünk, amikor megnyílik a MEX Magic hozzáférés.
            </p>

            <form
              onSubmit={onSubmit}
              data-testid="waitlist-form"
              className="flex flex-col gap-3"
            >
              <input
                type="text"
                placeholder="Keresztnév"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="waitlist-firstname"
                style={inputStyle}
                autoFocus
              />
              <input
                type="email"
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="waitlist-email"
                style={inputStyle}
              />

              <label
                className="flex items-start gap-2.5 mt-1 cursor-pointer select-none"
                style={{ fontSize: 12.5, color: "#3F365E", lineHeight: 1.5 }}
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                    if (e.target.checked) setConsentError(false);
                  }}
                  data-testid="waitlist-consent"
                  style={{
                    marginTop: 3,
                    width: 16,
                    height: 16,
                    accentColor: "#5e50f7",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                />
                <span>
                  Elfogadom az{" "}
                  <a
                    href="/adatkezelesi-tajekoztato"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[#5e50f7] hover:underline"
                  >
                    Adatkezelési tájékoztatót
                  </a>
                  , és hozzájárulok, hogy e-mailben megkapjam a MEX Magic várólistával és kapcsolódó kommunikációkkal kapcsolatos értesítéseket.
                </span>
              </label>
              {consentError && (
                <div
                  data-testid="waitlist-consent-error"
                  role="alert"
                  style={{ fontSize: 12, color: "#D62828", marginTop: -4, lineHeight: 1.4 }}
                >
                  Kérlek, fogadd el az adatkezelési feltételeket a feliratkozáshoz.
                </div>
              )}

              {formError && (
                <div
                  data-testid="waitlist-form-error"
                  role="alert"
                  style={{ fontSize: 12.5, color: "#D62828", lineHeight: 1.45, marginTop: -2 }}
                >
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="waitlist-submit"
                className="group inline-flex items-center justify-center gap-3 mt-1 px-8 py-4 rounded-2xl font-bold text-white transition-all hover:-translate-y-0.5"
                style={{
                  background:
                    "linear-gradient(135deg, #6E5BFF 0%, #5e50f7 30%, #7C3AED 65%, #4F46E5 100%)",
                  boxShadow:
                    "0 24px 60px rgba(94,80,247,0.48), 0 8px 18px rgba(124,58,237,0.32), 0 0 0 1px rgba(255,255,255,0.32) inset",
                  fontSize: 16,
                  opacity: canSubmit ? 1 : 0.55,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                }}
              >
                {submitting ? "Feliratkozás..." : "Feliratkozom a várólistára"}
                {!submitting && <span style={{ transition: "transform 0.15s ease" }}>→</span>}
              </button>
            </form>
          </>
        )}

        {/* Modal-local konfetti burst (CSS animation, NEM canvas, NEM
            fullscreen). 28 színes particle az 🎉 emoji környékéről lő ki
            felfelé szétterülő legyezőben, ~1.5s alatt elhalványul. A
            modal kártyán BELÜL renderelődik (z-index 20), így 100%-ban a
            popup ELŐTT, élesen láthatóan jelenik meg — független a
            backdrop-filter / overlay stacking contextjeitől. */}
        {success && <ModalConfettiBurst active={cannonOn} />}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
 * FŐKOMPONENS
 * ═══════════════════════════════════════════════════════════════════ */
export default function LandingHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginDenied, setLoginDenied] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [loginInitialMode, setLoginInitialMode] = useState("login");
  const [loginInitialEmail, setLoginInitialEmail] = useState("");
  const [loginResetToken, setLoginResetToken] = useState(null);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  // Egységes prémium belépési élmény: ha valaki direktben nyitja meg a
  // /login URL-t, automatikusan ezt a modalt nyitjuk a landing fölött.
  // Támogatott query paramok:
  //   ?reset_token=... → reset módban nyílik
  //   ?email=...&mode=register → register mód, előre kitöltött e-maillel
  //   ?login=1 → sima login mód
  //   ?denied=... → allowlist-elutasítás megjelenítése
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const resetToken = params.get("reset_token");
    const modeParam = params.get("mode");
    const emailParam = params.get("email");
    const kind = params.get("denied");
    const msg = params.get("msg");

    const isLoginRoute = location.pathname === "/login" || location.search.includes("login=1");

    if (resetToken) {
      setLoginResetToken(resetToken);
      setLoginInitialMode("reset");
      setLoginOpen(true);
    } else if (modeParam === "register") {
      setLoginResetToken(null);
      setLoginInitialMode("register");
      setLoginInitialEmail(emailParam || "");
      setLoginOpen(true);
    } else if (isLoginRoute) {
      setLoginResetToken(null);
      setLoginInitialMode("login");
      setLoginInitialEmail(emailParam || "");
      setLoginOpen(true);
    }

    if (kind) {
      setLoginDenied({ kind, message: msg || "" });
      setLoginOpen(true);
    }
  }, [location.pathname, location.search]);

  const handleWaitlistClick = () => {
    setWaitlistOpen(true);
  };
  const handleLoginClick = () => {
    setLoginDenied(null);
    setLoginOpen(true);
  };
  const handleLoginClose = () => {
    setLoginOpen(false);
    setLoginDenied(null);
    setLoginResetToken(null);
    setLoginInitialEmail("");
    setLoginInitialMode("login");
    // Ha /login direkt URL-en érkezett a user, vagy speciális query paramokkal,
    // vigyük vissza a / -ra tiszta állapotra.
    const search = location.search || "";
    if (
      location.pathname === "/login" ||
      search.includes("denied=") ||
      search.includes("reset_token=") ||
      search.includes("mode=register") ||
      search.includes("login=1")
    ) {
      navigate("/", { replace: true });
    }
  };

  const bgStyle = useMemo(
    () => ({
      background:
        "radial-gradient(60% 50% at 75% 22%, rgba(94,80,247,0.12) 0%, rgba(255,255,255,0) 60%), radial-gradient(50% 40% at 20% 35%, rgba(41,190,218,0.08) 0%, rgba(255,255,255,0) 65%), radial-gradient(60% 70% at 90% 90%, rgba(255,219,92,0.07) 0%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #F8F6FE 0%, #FBFAFF 100%)",
      minHeight: "100vh",
    }),
    []
  );

  return (
    <div data-testid="landing-page" style={bgStyle} className="font-sans relative overflow-x-hidden" data-font="geist">
      {/* Finom rácsháló */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(94,80,247,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(94,80,247,0.05) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)",
        }}
      />

      <div className="relative z-10">
        {/* ─── HERO + HEADER COMMON WRAPPER ───
            A MagicalBackdrop ennek a wrappernek a hátterét adja, így a
            kolibri-glow / lavender gradient SEAMLESS-en felfut a header
            mögé is — nincs külön „világos sáv" a tetején. */}
        <div className="relative">
          {/* Backdrop: header + hero közös hátterét egyetlen rétegként rendereli */}
          <MagicalBackdrop />

          <div className="relative z-20">
            <LandingHeader onLoginClick={handleLoginClick} />
          </div>

          {/* HERO – tartalom rétegek a backdrop felett, BottomWave a section alján */}
          <section
            data-testid="hero-section"
            className="relative px-6 sm:px-10 lg:px-16 pt-2 lg:pt-3 pb-24 lg:pb-36 overflow-hidden z-10"
          >
            <div className="relative max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[46%_54%] gap-10 lg:gap-12 items-center">
            {/* ─── BAL: szöveg + CTA (vertikálisan centered desktopon) ─── */}
            <div className="pt-2 lg:pt-3 relative z-10 lh-left-col">
              <div
                data-testid="landing-kicker"
                className="inline-block text-[12.5px] sm:text-[13.5px] font-semibold tracking-[0.20em] uppercase mb-4 lh-kicker"
                style={{ color: "#3C3489" }}
              >
                AI-alapú ügyfélszerző műhely
              </div>

              <h1
                data-testid="landing-headline"
                className="font-heading lh-headline"
                style={{
                  fontWeight: 700,
                  lineHeight: 1.04,
                  letterSpacing: "-0.035em",
                  color: "#2B2545",
                }}
              >
                <span
                  className="block lh-headline-anim"
                  style={{
                    fontSize: "clamp(32px, 4.0vw, 56px)",
                    position: "relative",
                    minHeight: "1.25em",
                    lineHeight: 1.18,
                    paddingBottom: "0.12em",
                    marginBottom: "0.02em",
                    overflow: "visible",
                  }}
                >
                  <AnimatedHeadline />
                </span>
                <span
                  className="block lh-headline-fixed"
                  style={{
                    fontWeight: 700,
                    fontSize: "clamp(30px, 3.8vw, 52px)",
                    color: "#1B1733",
                    letterSpacing: "-0.035em",
                    lineHeight: 1.1,
                  }}
                  data-testid="landing-headline-fixed"
                >
                  …pár kattintással.
                </span>
              </h1>

              <p
                data-testid="landing-subtitle"
                className="mt-5 mb-8 max-w-[560px] lh-subtitle"
                style={{ color: "#433C66", fontSize: "clamp(15.5px, 1.15vw, 19px)", lineHeight: 1.6 }}
              >
                A MEX Magic a Te valódi hangodat hangolja össze az ügyfeleid legmélyebb vágyaival. Így a marketinged
                megszűnik zajnak lenni<br className="hidden lg:inline" /> - tiszta rezonanciává válik.
              </p>

              <button
                onClick={handleWaitlistClick}
                data-testid="landing-cta"
                className="group inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-[20px] rounded-2xl font-bold text-white transition-all hover:-translate-y-0.5 lh-cta"
                style={{
                  background:
                    "linear-gradient(135deg, #6E5BFF 0%, #5e50f7 30%, #7C3AED 65%, #4F46E5 100%)",
                  boxShadow:
                    "0 24px 60px rgba(94,80,247,0.48), 0 8px 18px rgba(124,58,237,0.32), 0 0 0 1px rgba(255,255,255,0.32) inset",
                  fontSize: "clamp(16px, 1.15vw, 18px)",
                }}
              >
                Feliratkozom a várólistára
                <ArrowRight size={20} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
              </button>

              {/* Powered by - a gomb ALATT, sárga villámmal */}
              <div
                data-testid="landing-powered-by"
                className="mt-6 flex items-center gap-2 text-[#433C66] lh-powered"
                style={{ fontSize: "clamp(13px, 0.95vw, 15px)" }}
              >
                <Lightning size={16} weight="fill" color="#FFB800" />
                <span className="font-medium">Powered by MEX Organic Resonance Engine™</span>
              </div>
            </div>

            {/* ─── JOBB: csak kolibri + kártyák (NEM háttér, NEM panel) ─── */}
            <div className="relative">
              <HeroVisual />
            </div>
          </div>

          {/* Lágy hullám-elválasztó a contact szekció felé — csak a hero
              alján, nem a header alatt. */}
          <BottomWave />
        </section>
        </div>{/* /HERO + HEADER COMMON WRAPPER */}

        {/* CONTACT - külön háttérszínnel a hero-tól elválasztva */}
        <section
          id="contact-section"
          data-testid="contact-section"
          className="relative px-6 sm:px-10 lg:px-16 pt-20 lg:pt-28 pb-20 lg:pb-24"
          style={{
            background:
              "linear-gradient(180deg, #F5EEFE 0%, #EBE4FA 45%, #E5DDF6 100%)",
          }}
        >
          <ContactForm />
        </section>

        {/* MINIMÁL FOOTER — jogi link sor */}
        <footer
          data-testid="landing-footer"
          className="px-6 sm:px-10 lg:px-16 py-7 text-center text-[12.5px] text-[#7B7596]"
          style={{
            background: "#EDE3F6",
            borderTop: "1px solid rgba(94,80,247,0.06)",
          }}
        >
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <a href="/aszf" data-testid="footer-link-aszf" className="hover:text-[#5e50f7] transition-colors">
              ÁSZF
            </a>
            <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
            <span>MEX Magic • All rights reserved.</span>
            <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
            <a href="/adatkezelesi-tajekoztato" data-testid="footer-link-gdpr" className="hover:text-[#5e50f7] transition-colors">
              Adatkezelési tájékoztató
            </a>
          </div>
        </footer>
      </div>

      {/* Belépési modal — Google OAuth */}
      <LoginModal
        open={loginOpen}
        onClose={handleLoginClose}
        denied={loginDenied}
        initialMode={loginInitialMode}
        initialEmail={loginInitialEmail}
        resetToken={loginResetToken}
      />
      <WaitlistModal open={waitlistOpen} onClose={() => setWaitlistOpen(false)} />
    </div>
  );
}
